import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAppState } from '../hooks/useAppState';
import { Shield, ArrowRight, ArrowLeft, Building2, MapPin, Users, Check } from 'lucide-react';

const SECTORES_SCIAN = [
  '11 — Agricultura, cría y explotación de animales',
  '21 — Minería',
  '22 — Generación y distribución de energía eléctrica, agua y gas',
  '23 — Construcción',
  '31-33 — Industrias manufactureras',
  '43 — Comercio al por mayor',
  '46 — Comercio al por menor',
  '48-49 — Transportes, correos y almacenamiento',
  '51 — Información en medios masivos',
  '52 — Servicios financieros y de seguros',
  '53 — Servicios inmobiliarios y de alquiler',
  '54 — Servicios profesionales, científicos y técnicos',
  '55 — Corporativos',
  '56 — Servicios de apoyo a los negocios',
  '61 — Servicios educativos',
  '62 — Servicios de salud y asistencia social',
  '71 — Servicios de esparcimiento y recreativos',
  '72 — Alojamiento y preparación de alimentos',
  '81 — Otros servicios',
  '93 — Actividades gubernamentales',
];

const ESTADOS_MX = [
  'Aguascalientes','Baja California','Baja California Sur','Campeche','Chiapas','Chihuahua',
  'Ciudad de México','Coahuila','Colima','Durango','Estado de México','Guanajuato','Guerrero',
  'Hidalgo','Jalisco','Michoacán','Morelos','Nayarit','Nuevo León','Oaxaca','Puebla',
  'Querétaro','Quintana Roo','San Luis Potosí','Sinaloa','Sonora','Tabasco','Tamaulipas',
  'Tlaxcala','Veracruz','Yucatán','Zacatecas',
];

const REFERRAL_OPTIONS = [
  { id: 'google', label: 'Búsqueda en Google', icon: '🔍' },
  { id: 'recomendacion', label: 'Recomendación', icon: '🤝' },
  { id: 'redes', label: 'Redes sociales', icon: '📱' },
  { id: 'consultor', label: 'Mi consultor / asesor', icon: '💼' },
  { id: 'stps', label: 'Inspección STPS', icon: '📋' },
  { id: 'otro', label: 'Otro', icon: '💡' },
];

export default function OnboardingPage() {
  const { user, logout } = useAuth();
  const { state } = useAppState();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});

  // If already has empresa + centro, go to dashboard
  useEffect(() => {
    if (state.empresaId && state.centrosTrabajo?.length > 0) {
      navigate('/dashboard', { replace: true });
    }
  }, [state.empresaId, state.centrosTrabajo, navigate]);

  // Si ya se completó este wizard antes (ej. el usuario ya pagó pero se
  // interrumpió antes de terminar de crear la empresa/centro), no lo repitas
  // — manda directo a /planes, que reanuda automáticamente.
  useEffect(() => {
    if (localStorage.getItem('nom035_pending_onboarding')) {
      navigate('/planes', { replace: true });
    }
  }, [navigate]);

  // Allow scroll
  useEffect(() => {
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    const root = document.getElementById('root');
    if (root) root.style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      if (root) root.style.overflow = '';
    };
  }, []);

  // Form state
  const [empresa, setEmpresa] = useState({
    razonSocial: '', rfc: '', sectorSCIAN: '', actividadEconomica: '',
    representanteLegal: '', cargoRepresentante: '',
    responsableEvaluacion: '', cargoResponsable: '',
    totalCentros: '1', whatsapp: '',
  });
  const [centro, setCentro] = useState({
    nombreCentro: '', numTrabajadores: '', actividadPrincipal: '',
    calle: '', numExterior: '', colonia: '', cp: '', municipio: '', estado: '',
  });
  const [referral, setReferral] = useState('');

  const setE = (k, v) => { setEmpresa(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: null })); };
  const setC = (k, v) => { setCentro(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: null })); };
  const validarRFC = (rfc) => !rfc.trim() || /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/i.test(rfc);

  const totalSteps = 4;
  const firstName = user?.displayName?.split(' ')[0] || '';

  // Step validation
  const validateStep = () => {
    const errs = {};
    if (step === 1) {
      if (!empresa.razonSocial.trim()) errs.razonSocial = 'Requerido';
      if (empresa.rfc.trim() && !validarRFC(empresa.rfc)) errs.rfc = 'RFC inválido';
    }
    if (step === 2) {
      if (!centro.nombreCentro.trim()) errs.nombreCentro = 'Requerido';
      if (!centro.numTrabajadores || parseInt(centro.numTrabajadores) < 1) errs.numTrabajadores = 'Requerido';
      if (!centro.calle.trim()) errs.calle = 'Requerido';
      if (!centro.colonia.trim()) errs.colonia = 'Requerido';
      if (!centro.cp.trim()) errs.cp = 'Requerido';
      if (!centro.municipio.trim()) errs.municipio = 'Requerido';
      if (!centro.estado) errs.estado = 'Requerido';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  // Final: save onboarding data to localStorage → navigate to /planes
  const handleFinish = () => {
    const pendingData = {
      empresa: {
        nombre: empresa.razonSocial,
        razonSocial: empresa.razonSocial,
        rfc: empresa.rfc,
        sectorSCIAN: empresa.sectorSCIAN,
        actividadEconomica: empresa.actividadEconomica,
        representanteLegal: empresa.representanteLegal,
        cargoRepresentante: empresa.cargoRepresentante,
        responsableEvaluacion: empresa.responsableEvaluacion,
        cargoResponsable: empresa.cargoResponsable,
        referral: referral,
        totalCentros: parseInt(empresa.totalCentros) || 1,
        telefono: empresa.whatsapp,
        whatsapp: empresa.whatsapp,
      },
      centro: {
        ...centro,
        id: 'ct_' + Date.now(),
        numTrabajadores: parseInt(centro.numTrabajadores, 10),
        trabajadores: [],
      },
    };
    localStorage.setItem('nom035_pending_onboarding', JSON.stringify(pendingData));
    navigate('/planes');
  };

  // ── Styles ──────────────────────────────────────────────
  const accent = '#6366f1';
  const wrap = { minHeight: '100vh', background: 'linear-gradient(135deg, #eef2ff 0%, #f8fafc 50%, #ede9fe 100%)', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' };
  const cardWrap = { maxWidth: 580, margin: '0 auto', padding: '32px 20px 60px' };
  const inputStyle = { width: '100%', padding: '13px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, color: '#1e293b', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' };
  const inputFocus = { borderColor: accent };
  const inputErr = (k) => errors[k] ? { borderColor: '#ef4444' } : {};
  const labelStyle = { fontSize: '0.78rem', fontWeight: 600, color: '#64748b', marginBottom: 6, display: 'block' };
  const errText = (k) => errors[k] ? <div style={{ fontSize: '0.72rem', color: '#ef4444', marginTop: 3 }}>{errors[k]}</div> : null;

  const numTrab = parseInt(centro.numTrabajadores) || 0;
  const obligLabel = numTrab <= 0 ? '' : numTrab <= 15 ? 'Guía I' : numTrab <= 50 ? 'Guía I + II' : 'Guía I + III';

  // ── Render ──────────────────────────────────────────────
  return (
    <div style={wrap}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 28px', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={20} style={{ color: accent }} />
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>NOM-035</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '0.82rem', color: '#64748b' }}>{user?.email}</span>
          <button onClick={async () => { await logout(); navigate('/login'); }} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '0.78rem', cursor: 'pointer' }}>Cerrar sesion</button>
        </div>
      </div>

      <div style={cardWrap}>
        {/* Progress */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Paso {step} de {totalSteps}</span>
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: accent }}>{Math.round((step / totalSteps) * 100)}%</span>
          </div>
          <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: `linear-gradient(90deg, ${accent}, #8b5cf6)`, borderRadius: 3, width: `${(step / totalSteps) * 100}%`, transition: 'width 0.4s ease' }} />
          </div>
        </div>

        {/* ── Step 1: Empresa ──────────────────────────── */}
        {step === 1 && (
          <div>
            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#0f172a', margin: '0 0 6px' }}>
                {firstName ? `${firstName}, registra tu empresa` : 'Registra tu empresa'}
              </h1>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>Datos corporativos de la organizacion.</p>
            </div>
            <div style={{ background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 2px 16px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={labelStyle}>Razon social *</label>
                  <input style={{ ...inputStyle, ...inputErr('razonSocial') }} value={empresa.razonSocial} onChange={e => setE('razonSocial', e.target.value)} placeholder="Ej. Grupo Industrial Novatech S.A. de C.V." />
                  {errText('razonSocial')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>RFC</label>
                    <input style={{ ...inputStyle, ...inputErr('rfc') }} value={empresa.rfc} onChange={e => setE('rfc', e.target.value)} placeholder="GIN980512AB3" />
                    {errText('rfc')}
                  </div>
                  <div>
                    <label style={labelStyle}>Sector economico</label>
                    <select style={inputStyle} value={empresa.sectorSCIAN} onChange={e => setE('sectorSCIAN', e.target.value)}>
                      <option value="">Seleccionar...</option>
                      {SECTORES_SCIAN.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Actividad economica principal</label>
                  <input style={inputStyle} value={empresa.actividadEconomica} onChange={e => setE('actividadEconomica', e.target.value)} placeholder="Ej. Fabricacion de componentes industriales" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Numero total de centros de trabajo</label>
                    <input style={inputStyle} type="number" min="1" value={empresa.totalCentros} onChange={e => setE('totalCentros', e.target.value)} placeholder="1" />
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 }}>Plantas, oficinas, sucursales, almacenes, etc.</div>
                  </div>
                  <div>
                    <label style={labelStyle}>WhatsApp</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 14, top: 13, fontSize: '1rem' }}>📱</span>
                      <input style={{ ...inputStyle, paddingLeft: 38 }} type="tel" value={empresa.whatsapp} onChange={e => setE('whatsapp', e.target.value)} placeholder="55 1234 5678" />
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 }}>Para enviarte notificaciones y soporte.</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Representante legal</label>
                    <input style={inputStyle} value={empresa.representanteLegal} onChange={e => setE('representanteLegal', e.target.value)} placeholder="Nombre completo" />
                  </div>
                  <div>
                    <label style={labelStyle}>Cargo</label>
                    <input style={inputStyle} value={empresa.cargoRepresentante} onChange={e => setE('cargoRepresentante', e.target.value)} placeholder="Director General" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Centro ──────────────────────────── */}
        {step === 2 && (
          <div>
            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#0f172a', margin: '0 0 6px' }}>
                Centro de trabajo
              </h1>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>{empresa.razonSocial} — las obligaciones NOM-035 aplican por centro.</p>
            </div>
            <div style={{ background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 2px 16px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Nombre del centro *</label>
                    <input style={{ ...inputStyle, ...inputErr('nombreCentro') }} value={centro.nombreCentro} onChange={e => setC('nombreCentro', e.target.value)} placeholder="Ej. Planta Queretaro" />
                    {errText('nombreCentro')}
                  </div>
                  <div>
                    <label style={labelStyle}>No. trabajadores *</label>
                    <input style={{ ...inputStyle, ...inputErr('numTrabajadores') }} type="number" min="1" value={centro.numTrabajadores} onChange={e => setC('numTrabajadores', e.target.value)} placeholder="65" />
                    {errText('numTrabajadores')}
                  </div>
                </div>
                {obligLabel && (
                  <div style={{ padding: '10px 14px', background: `${accent}08`, borderRadius: 10, border: `1px solid ${accent}20`, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Users size={16} style={{ color: accent }} />
                    <span style={{ fontSize: '0.8rem', color: accent, fontWeight: 500 }}>Obligaciones: {obligLabel}</span>
                  </div>
                )}
                <div>
                  <label style={labelStyle}>Calle *</label>
                  <input style={{ ...inputStyle, ...inputErr('calle') }} value={centro.calle} onChange={e => setC('calle', e.target.value)} placeholder="Av. Industria 456" />
                  {errText('calle')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>No. exterior</label>
                    <input style={inputStyle} value={centro.numExterior} onChange={e => setC('numExterior', e.target.value)} placeholder="456" />
                  </div>
                  <div>
                    <label style={labelStyle}>Colonia *</label>
                    <input style={{ ...inputStyle, ...inputErr('colonia') }} value={centro.colonia} onChange={e => setC('colonia', e.target.value)} placeholder="Parque Industrial" />
                    {errText('colonia')}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>CP *</label>
                    <input style={{ ...inputStyle, ...inputErr('cp') }} value={centro.cp} onChange={e => setC('cp', e.target.value)} placeholder="76100" />
                    {errText('cp')}
                  </div>
                  <div>
                    <label style={labelStyle}>Municipio *</label>
                    <input style={{ ...inputStyle, ...inputErr('municipio') }} value={centro.municipio} onChange={e => setC('municipio', e.target.value)} placeholder="Queretaro" />
                    {errText('municipio')}
                  </div>
                  <div>
                    <label style={labelStyle}>Estado *</label>
                    <select style={{ ...inputStyle, ...inputErr('estado') }} value={centro.estado} onChange={e => setC('estado', e.target.value)}>
                      <option value="">Seleccionar...</option>
                      {ESTADOS_MX.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                    {errText('estado')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 3: Referral ────────────────────────── */}
        {step === 3 && (
          <div>
            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#0f172a', margin: '0 0 6px' }}>
                Una ultima pregunta
              </h1>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>Nos ayuda a mejorar.</p>
            </div>
            <div style={{ background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 2px 16px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}>
              <p style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b', margin: '0 0 20px' }}>Como te enteraste de nosotros?</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {REFERRAL_OPTIONS.map(opt => (
                  <button key={opt.id} onClick={() => setReferral(opt.id)} style={{
                    padding: '16px 18px', borderRadius: 12, border: '2px solid',
                    borderColor: referral === opt.id ? accent : '#e5e7eb',
                    background: referral === opt.id ? `${accent}08` : '#fff',
                    color: referral === opt.id ? accent : '#475569',
                    fontSize: '0.9rem', fontWeight: referral === opt.id ? 600 : 400,
                    cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>{opt.icon}</span>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Step 4: Resumen ─────────────────────────── */}
        {step === 4 && (
          <div>
            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#0f172a', margin: '0 0 6px' }}>
                Todo listo
              </h1>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>Confirma tus datos y elige tu plan.</p>
            </div>
            <div style={{ background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 2px 16px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Empresa summary */}
                <div style={{ padding: '14px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Building2 size={16} style={{ color: accent }} />
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Empresa</span>
                  </div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>{empresa.razonSocial}</div>
                  {empresa.rfc && <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 2 }}>RFC: {empresa.rfc}</div>}
                  {empresa.sectorSCIAN && <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 2 }}>{empresa.sectorSCIAN}</div>}
                  <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 2 }}>{empresa.totalCentros || 1} centro{(parseInt(empresa.totalCentros) || 1) > 1 ? 's' : ''} de trabajo</div>
                </div>

                {/* Centro summary */}
                <div style={{ padding: '14px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <MapPin size={16} style={{ color: '#10b981' }} />
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Centro de trabajo</span>
                  </div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>{centro.nombreCentro}</div>
                  <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 2 }}>{centro.numTrabajadores} trabajadores — {obligLabel}</div>
                  <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 2 }}>{centro.calle}{centro.numExterior ? ' No.' + centro.numExterior : ''}, {centro.colonia}, {centro.municipio}, {centro.estado} CP {centro.cp}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Navigation ──────────────────────────────── */}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          {step > 1 && (
            <button onClick={handleBack} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '14px 24px',
              background: '#fff', color: '#475569', border: '1px solid #e2e8f0',
              borderRadius: 10, fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer',
            }}>
              <ArrowLeft size={16} /> Atras
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < totalSteps ? (
            <button onClick={handleNext} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '14px 32px',
              background: accent, color: '#fff', border: 'none', borderRadius: 10,
              fontSize: '0.92rem', fontWeight: 600, cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(99,102,241,0.25)',
            }}>
              Siguiente <ArrowRight size={16} />
            </button>
          ) : (
            <button onClick={handleFinish} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '14px 32px',
              background: '#10b981', color: '#fff', border: 'none', borderRadius: 10,
              fontSize: '0.92rem', fontWeight: 600, cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(16,185,129,0.25)',
            }}>
              Confirmar y elegir plan <Check size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
